import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { LocalStorage } from '../utilities/localStorage';
import { navigateToLogin } from '../utilities/navigation';
import { setApiError } from './app/setApiError/setApiError.slice';
import { setMe } from './app/setMe/setMe.slice';
import { logout } from './auth/logout/logout.slice';
import { LOGOUT_PATH } from './auth/logout/logout.repository';
import { Errors } from './errors';
import {
  AUTO_CANCEL_ENABLED,
  CancelableRequestConfig,
  registerRequest,
  unregisterRequest,
} from './requestRegistry';

let dispatch: ThunkDispatch<unknown, unknown, AnyAction>;

// Cerez dustugunde dashboard'un paralel istekleri toplu 401 doner. Bu bayrak
// olmadan her biri ayri bir POST /auth/logout ve ayri bir yonlendirme tetikler.
let isForcedLogoutInFlight = false;

export function resetForcedLogoutGuard(): void {
  isForcedLogoutInFlight = false;
}

const acceptLanguageMap: Record<string, string> = {
  tr: 'tr-TR,tr;q=0.9,en-US;q=0.8,en;q=0.7',
  en: 'en-US,en;q=0.9,tr-TR;q=0.8,tr;q=0.7',
};

// Oturum HttpOnly cerezde (hf_at) tasiniyor. Panel ve API farkli origin'lerde
// oldugu icin cerezin gonderilmesi/kaydedilmesi bu bayraga bagli.
axios.defaults.withCredentials = true;

axios.interceptors.request.use((request) => {
  const lang = LocalStorage.get(LocalStorage.i18nextLng);

  if (request.headers) {
    request.headers['Accept-Language'] =
      acceptLanguageMap[lang ?? ''] ?? acceptLanguageMap['tr'];
  }

  // Authorization header'i BILEREK eklenmiyor: backend once header'a bakiyor,
  // orada bayat bir deger bulursa cereze dusmuyor ve istegi 401 ile reddediyor.

  const config = request as CancelableRequestConfig;
  if (AUTO_CANCEL_ENABLED && !config.signal && !config.skipAutoCancel) {
    const controller = new AbortController();
    config.signal = controller.signal;
    config.__abortController = controller;
    registerRequest(controller, window.location.pathname);
  }

  return request;
});

function isLogoutRequest(error: any): boolean {
  return !!error?.config?.url?.endsWith(LOGOUT_PATH);
}

function forceLogout(): void {
  if (isForcedLogoutInFlight) {
    return;
  }
  // Bilerek burada sifirlanmiyor: eski sayfadan gec donen 401'ler ikinci bir
  // cikis istegi tetiklemesin. Bayragi yalnizca basarili login kaldirir
  // (bkz. login.slice -> resetForcedLogoutGuard).
  isForcedLogoutInFlight = true;

  Promise.resolve(dispatch(logout())).then(() => {
    navigateToLogin();
  });
}

export function setResponseInterceptor(
  appDispatch: ThunkDispatch<unknown, unknown, AnyAction>
) {
  dispatch = appDispatch;
  axios.interceptors.response.use(
    (response) => {
      unregisterRequest(
        (response.config as CancelableRequestConfig).__abortController
      );
      return response;
    },
    async (error) => {
      unregisterRequest(
        (error?.config as CancelableRequestConfig)?.__abortController
      );

      // Sayfa degisiminde iptal edilen istek. Kullaniciya gosterilecek bir hata
      // degil: setApiError, logout ve log YOK. Bu kontrol her seyden once gelmeli,
      // cunku iptal hatasinda error.response yoktur ve asagidaki jenerik dal
      // her gezinmede hata modali acardi.
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      // Cikis istegi kendi kendini tetiklemesin (ag hatasi da bu dala duser).
      if (isLogoutRequest(error)) {
        return Promise.reject(error);
      }

      // Auth bootstrap'i (checkToken -> getMe) anonim her sayfa yuklemesinde 401
      // alir. Silinecek cerez yok, yonlendirilecek yer yok.
      const isSilentAuthProbe = (error?.config as CancelableRequestConfig)
        ?.skipForcedLogout;

      if (Errors.isAuthError(error)) {
        if (!isSilentAuthProbe) {
          forceLogout();
        } else {
          dispatch(setMe());
        }
      } else if (Errors.isMilesServiceError(error)) {
        dispatch(setApiError(Errors.getErrorDtoFromApiError(error)));
        forceLogout();
      } else if (
        !(error?.config as CancelableRequestConfig)?.skipGlobalErrorModal
      ) {
        dispatch(setApiError(Errors.getErrorDtoFromApiError(error)));
      }
      console.log('ERROR: ', error);
      return Promise.reject(error);
    }
  );
}

export default axios;
